import { useState, useCallback, useEffect, useRef } from 'react';

/**
 * Custom hook for handling context menus
 * @returns {Object} Context menu utilities and state
 */
export const useContextMenu = () => {
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
  });
  const menuRef = useRef(null);

  // Log when the hook is used
  useEffect(() => {
    console.log('useContextMenu hook initialized');
    return () => console.log('useContextMenu hook cleanup');
  }, []);

  const showContextMenu = useCallback((event) => {
    try {
      console.log('Context menu triggered', { clientX: event.clientX, clientY: event.clientY });
      event.preventDefault();
      event.stopPropagation();
      
      // Calculate position to ensure the menu stays within viewport
      const menuWidth = 200; // Approximate menu width
      const menuHeight = 150; // Approximate menu height
      
      const x = Math.min(
        event.clientX,
        window.innerWidth - menuWidth - 10 // Add some padding from the edge
      );
      
      const y = Math.min(
        event.clientY,
        window.innerHeight - menuHeight - 10 // Add some padding from the edge
      );
      
      console.log('Setting context menu position', { x, y });
      setContextMenu({ 
        visible: true, 
        x: Math.max(10, x), // Ensure we don't go off the left edge
        y: Math.max(10, y)  // Ensure we don't go off the top edge
      });
    } catch (error) {
      console.error('Error in showContextMenu:', error);
    }
  }, []);

  const hideContextMenu = useCallback(() => {
    console.log('Hiding context menu');
    setContextMenu(prev => ({
      ...prev, 
      visible: false 
    }));
  }, []);

  // Close menu when clicking outside or pressing Escape
  useEffect(() => {
    if (!contextMenu.visible) return;

    const handleClickOutside = (event) => {
      console.log('Click outside detected');
      hideContextMenu();
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        console.log('Escape key pressed');
        hideContextMenu();
      }
    };

    // Add a small delay to prevent immediate closing
    const timer = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
      document.addEventListener('contextmenu', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('contextmenu', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [contextMenu.visible, hideContextMenu]);

  const getMenuProps = useCallback((customProps = {}) => ({
    onContextMenu: (e) => {
      console.log('Context menu event fired');
      showContextMenu(e);
    },
    ...customProps,
  }), [showContextMenu]);

  const getMenuStyle = useCallback(() => ({
    position: 'fixed',
    top: `${contextMenu.y}px`,
    left: `${contextMenu.x}px`,
    zIndex: 9999,
    display: contextMenu.visible ? 'block' : 'none',
    backgroundColor: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '0.5rem',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    minWidth: '200px',
    padding: '0.5rem 0',
    transition: 'opacity 0.2s ease, transform 0.2s ease',
    transform: contextMenu.visible ? 'scale(1)' : 'scale(0.95)',
    opacity: contextMenu.visible ? 1 : 0,
    pointerEvents: contextMenu.visible ? 'auto' : 'none',
  }), [contextMenu.visible, contextMenu.x, contextMenu.y]);

  return {
    contextMenu,
    showContextMenu,
    hideContextMenu,
    getMenuProps,
    getMenuStyle,
    menuRef,
  };
};

export default useContextMenu;
